/**
 * Email Lead Categorizer Service
 * Processes incoming emails, categorizes by keywords, extracts contact info
 */

import { submitLeadToFirebase } from './firebase.js';
import { sendLeadNotifications } from './notifications.js';
import { v4 as uuidv4 } from 'uuid';

class EmailLeadCategorizer {
  constructor() {
    this.leadCategories = {
      'FSBO': {
        keywords: ['fsbo', 'for sale by owner', 'by owner', 'owner financing', 'no realtor', 'no agent'],
        priority: 9,
        color: '#dc3545', // red
        description: 'For Sale By Owner'
      },
      'Fixer Upper': {
        keywords: ['fixer upper', 'fixer-upper', 'handyman special', 'needs work', 'tlc', 'as-is', 'cash only'],
        priority: 8,
        color: '#fd7e14', // orange  
        description: 'Needs Repairs/TLC'
      },
      'Motivated Seller': {
        keywords: ['motivated', 'must sell', 'quick sale', 'fast closing', 'relocating', 'job transfer'],
        priority: 8,
        color: '#ffc107', // yellow
        description: 'Motivated to Sell'
      },
      'Distressed': {
        keywords: ['foreclosure', 'pre-foreclosure', 'divorce', 'estate sale', 'probate', 'bankruptcy'],
        priority: 9,
        color: '#dc3545', // red
        description: 'Distressed Situation'
      },
      'Investor Special': {
        keywords: ['investor special', 'investment property', 'rental property', 'flip opportunity', 'below market'],
        priority: 7,
        color: '#20c997', // teal
        description: 'Investment Opportunity'
      },
      'Vacant Property': {
        keywords: ['vacant', 'empty', 'unoccupied', 'needs occupant'],
        priority: 6,
        color: '#6610f2', // purple
        description: 'Vacant Property'
      },
      'Standard Listing': {
        keywords: [], // default category
        priority: 3,
        color: '#6c757d', // gray
        description: 'Standard Property Listing'
      }
    };
    
    this.processedEmails = new Set();
  }

  /**
   * Process incoming email and create lead
   * @param {Object} email - Email data
   * @param {string} email.subject - Email subject line
   * @param {string} email.body - Email body content
   * @param {string} email.from - Sender email
   * @param {string} email.source - Email source (zillow, craigslist, etc.)
   * @returns {Promise<Object|null>} - Created lead or null
   */
  async processEmail(email) {
    const { subject, body, from, source = 'email' } = email;
    const fullContent = `${subject} ${body}`.toLowerCase();
    
    // Generate unique ID to prevent duplicates
    const emailHash = this.generateEmailHash(subject, body);
    if (this.processedEmails.has(emailHash)) {
      console.log('⚠️ Email already processed:', subject);
      return null;
    }

    try {
      console.log('📧 Processing email:', subject);
      
      // Step 1: Categorize by keywords
      const category = this.categorizeByKeywords(fullContent);
      console.log('🏷️ Category:', category.name);
      
      // Step 2: Extract property info
      const propertyInfo = this.extractPropertyInfo(fullContent);
      console.log('🏠 Extracted info:', propertyInfo);
      
      // Step 3: Extract contact info
      const contactInfo = this.extractContactInfo(fullContent);
      console.log('📞 Contact info:', contactInfo);
      
      // Step 4: Create lead object
      const lead = this.createEmailLead({
        email, category, propertyInfo, contactInfo, source
      });
      
      // Step 5: Save to Firebase
      const leadId = await submitLeadToFirebase(lead);
      if (!leadId) return null;
      
      lead.id = leadId;
      
      // Step 6: Send notification if high priority
      if (category.priority >= 7) {
        await this.sendEmailLeadNotification(lead, category);
      }
      
      this.processedEmails.add(emailHash);
      console.log(`✅ Email lead created: ${leadId} (${category.name})`);
      
      return lead;
      
    } catch (error) {
      console.error('❌ Error processing email:', error);
      return null;
    }
  }

  /**
   * Categorize email content by keywords
   */
  categorizeByKeywords(content) {
    let bestMatch = { name: 'Standard Listing', ...this.leadCategories['Standard Listing'], matchCount: 0 };
    
    for (const [categoryName, categoryData] of Object.entries(this.leadCategories)) {
      if (categoryName === 'Standard Listing') continue;
      
      const matchCount = categoryData.keywords.filter(keyword => 
        content.includes(keyword)
      ).length;
      
      if (matchCount > bestMatch.matchCount) {
        bestMatch = { 
          name: categoryName, 
          ...categoryData, 
          matchCount 
        };
      }
    }
    
    return bestMatch;
  }

  /**
   * Extract property information from email content
   */
  extractPropertyInfo(content) {
    const info = {};
    
    // Extract price
    const priceMatch = content.match(/\$[\d,]+(?:\.\d{2})?/);
    if (priceMatch) {
      info.price = parseInt(priceMatch[0].replace(/[$,]/g, ''), 10);
      info.formattedPrice = priceMatch[0];
    }
    
    // Extract address patterns
    const addressPatterns = [
      /(\d+\s+[A-Za-z\s]+(?:st|street|ave|avenue|rd|road|ln|lane|dr|drive|ct|court|blvd|boulevard|way|pl|place)\.?)[,\s]+([A-Za-z\s]+)[,\s]+([A-Z]{2})\s+(\d{5})/i,
      /(\d+\s+[A-Za-z\s]+)[,\s]+([A-Za-z\s]+)[,\s]+([A-Z]{2})\s+(\d{5})/i
    ];
    
    for (const pattern of addressPatterns) {
      const addressMatch = content.match(pattern);
      if (addressMatch) {
        info.address = `${addressMatch[1]}, ${addressMatch[2]}, ${addressMatch[3]} ${addressMatch[4]}`;
        info.street = addressMatch[1];
        info.city = addressMatch[2];
        info.state = addressMatch[3];
        info.zip = addressMatch[4];
        break;
      }
    }
    
    // Extract bed/bath
    const bedroomMatch = content.match(/(\d+)\s*(?:bed|bedroom|br)/i);
    if (bedroomMatch) info.bedrooms = bedroomMatch[1];
    
    const bathroomMatch = content.match(/(\d+(?:\.\d+)?)\s*(?:bath|bathroom|ba)/i);
    if (bathroomMatch) info.bathrooms = bathroomMatch[1];
    
    // Extract square footage
    const sqftMatch = content.match(/(\d{1,3}(?:,\d{3})*)\s*(?:sq\.?\s*ft|sqft|square\s*feet)/i);
    if (sqftMatch) info.squareFootage = parseInt(sqftMatch[1].replace(/,/g, ''), 10);
    
    return info;
  }

  /**
   * Extract contact information from email content
   */
  extractContactInfo(content) {
    const contact = {};
    
    // Extract phone numbers - improved patterns
    const phonePatterns = [
      /\b(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})\b/g,
      /\b\((\d{3})\)[-.\s]?(\d{3})[-.\s]?(\d{4})\b/g,
      /\b(\d{3})\s+(\d{3})[-.\s](\d{4})\b/g
    ];
    
    const phones = [];
    for (const pattern of phonePatterns) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        let phoneStr;
        if (match.length > 3) {
          // For patterns with capture groups like (\d{3}) (\d{3}) (\d{4})
          phoneStr = match[1] + match[2] + match[3];
        } else {
          // For single match patterns
          phoneStr = match[0].replace(/[-.\s()]/g, '');
        }
        
        if (phoneStr.length === 10 && !phones.includes(phoneStr)) {
          phones.push(phoneStr);
        }
      }
    }
    
    if (phones.length > 0) {
      contact.phone = this.formatPhoneNumber(phones[0]);
      contact.allPhones = phones.map(p => this.formatPhoneNumber(p));
    }
    
    // Extract email addresses
    const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const emails = [...content.matchAll(emailPattern)].map(match => match[0]);
    
    if (emails.length > 0) {
      contact.email = emails[0];
      contact.allEmails = [...new Set(emails)];
    }
    
    // Extract names (simple pattern)
    const nameMatch = content.match(/(?:contact|call|email|from)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
    if (nameMatch) contact.name = nameMatch[1];
    
    return contact;
  }

  /**
   * Create lead object from email data
   */
  createEmailLead({ email, category, propertyInfo, contactInfo, source }) {
    return {
      // Lead basics
      type: 'email_lead',
      leadSource: `Email - ${source}`,
      campaign_name: `email_${category.name.toLowerCase().replace(/\s+/g, '_')}`,
      createdAt: new Date().toISOString(),
      
      // Email data
      originalSubject: email.subject,
      originalFrom: email.from,
      emailSource: source,
      
      // Category
      category: category.name,
      categoryPriority: category.priority,
      categoryColor: category.color,
      categoryDescription: category.description,
      keywordMatches: category.matchCount || 0,
      
      // Property info (with fallbacks)
      address: propertyInfo.address || 'Address not found',
      street: propertyInfo.street || '',
      city: propertyInfo.city || '',
      state: propertyInfo.state || '',
      zip: propertyInfo.zip || '',
      price: propertyInfo.price || 0,
      formattedPrice: propertyInfo.formattedPrice || '',
      bedrooms: propertyInfo.bedrooms || '',
      bathrooms: propertyInfo.bathrooms || '',
      squareFootage: propertyInfo.squareFootage || 0,
      
      // Contact info (with fallbacks)
      name: contactInfo.name || 'Property Owner',
      phone: contactInfo.phone || '',
      email: contactInfo.email || '',
      allPhones: contactInfo.allPhones || [],
      allEmails: contactInfo.allEmails || []
    };
  }

  /**
   * Send notification for high-priority email leads
   */
  async sendEmailLeadNotification(lead, category) {
    try {
      await sendLeadNotifications(lead, {
        source: `Email - ${lead.emailSource}`,
        pushoverTitle: `📧 ${category.name} Lead`,
        pushoverPriority: category.priority >= 9 ? 2 : 1,
        pushoverSound: 'persistent'
      });
    } catch (error) {
      console.error('❌ Email notification failed:', error);
    }
  }

  /**
   * Generate unique hash for email to prevent duplicates
   */
  generateEmailHash(subject, body) {
    const content = `${subject}${body}`.slice(0, 100); // First 100 chars
    return btoa(content).replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
  }

  /**
   * Format phone number to (XXX) XXX-XXXX
   */
  formatPhoneNumber(phone) {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  }

  /**
   * Get available lead categories
   */
  getCategories() {
    return this.leadCategories;
  }

  /**
   * Test email processing with sample data
   */
  async testWithSampleEmail() {
    const sampleEmail = {
      subject: 'FSBO: 3BR/2BA House - Must Sell Fast!',
      body: `
        Beautiful 3 bedroom, 2 bathroom house for sale by owner.
        Located at 1234 Main Street, Sacramento, CA 95814.
        Asking $450,000 - motivated seller, needs quick closing.
        Property needs some TLC but great bones. Cash offers preferred.
        Contact John Smith at (916) 555-0123 or johnsmith@email.com
        
        Details:
        - 1,200 sq ft
        - As-is condition
        - No realtor fees
        - Can close in 2 weeks
      `,
      from: 'johnsmith@email.com',
      source: 'test'
    };
    
    return this.processEmail(sampleEmail);
  }
}

// Export singleton
export const emailLeadCategorizer = new EmailLeadCategorizer();
export default emailLeadCategorizer;